/**
 * Header Component JS
 * Handles accessibility and UX enhancements for the site header
 * Bootstrap handles hamburger toggle and collapse/dropdown behavior via data-bs-* attributes
 */

// Import Bootstrap modules
import Collapse from 'bootstrap/js/dist/collapse';

class SiteHeader {
	constructor() {
		this.header = document.querySelector( '.site-header' );
		if ( !this.header ) return;

		this.nav = this.header.querySelector( '.navbar-collapse' );
		this.toggler = this.header.querySelector( '.site-header__toggler' );
		this.dropdownToggles = this.nav ? this.nav.querySelectorAll( '.site-header__link--dropdown' ) : [];
		this.dropdownItems = this.header.querySelectorAll( '.site-header__item--has-dropdown' );

		this.init();
	}

	init() {
		this.bindEvents();
		this.setupAccessibility();
		this.setupDropdowns();
		this.setupSearchIcon();
	}

	bindEvents() {
		// Close mobile menu on ESC key
		document.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Escape' && this.nav && this.nav.classList.contains( 'show' ) ) {
				this.closeMenu();
			}
		} );

		// Close mobile menu when clicking outside
		document.addEventListener( 'click', ( e ) => {
			if ( !this.nav || !this.nav.classList.contains( 'show' ) ) return;

			const clickedInside = this.header.contains( e.target );
			if ( !clickedInside ) {
				this.closeMenu();
			}
		} );

		// Track when mobile menu opens/closes to manage focus
		if ( this.nav ) {
			this.nav.addEventListener( 'shown.bs.collapse', () => {
				this.trapFocus();
			} );

			this.nav.addEventListener( 'hidden.bs.collapse', () => {
				this.releaseFocus();
			} );
		}
	}

	setupAccessibility() {
		// Ensure proper ARIA attributes are set
		// Bootstrap manages aria-expanded automatically on collapse triggers
		// This is just for additional enhancements if needed

		// Add role="navigation" if not already present
		if ( this.nav && !this.nav.getAttribute( 'role' ) ) {
			this.nav.setAttribute( 'role', 'navigation' );
		}
	}

	closeMenu() {
		if ( !this.nav || !this.toggler ) return;

		// Use Bootstrap's Collapse API to close
		const bsCollapse = Collapse.getInstance( this.nav );
		if ( bsCollapse ) {
			bsCollapse.hide();
		} else {
			// Fallback if instance doesn't exist
			const newCollapse = new Collapse( this.nav, {
				toggle: false
			} );
			newCollapse.hide();
		}

		// Return focus to toggler
		this.toggler.focus();
	}

	trapFocus() {
		// Get all focusable elements within the open menu
		const focusableElements = this.nav.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if ( focusableElements.length === 0 ) return;

		this.firstFocusable = focusableElements[0];
		this.lastFocusable = focusableElements[focusableElements.length - 1];

		// Focus first element in menu
		this.firstFocusable.focus();

		// Trap focus within menu
		this.focusTrapHandler = ( e ) => {
			if ( e.key !== 'Tab' ) return;

			if ( e.shiftKey ) {
				// Shift + Tab
				if ( document.activeElement === this.firstFocusable ) {
					e.preventDefault();
					this.lastFocusable.focus();
				}
			} else {
				// Tab
				if ( document.activeElement === this.lastFocusable ) {
					e.preventDefault();
					this.firstFocusable.focus();
				}
			}
		};

		this.nav.addEventListener( 'keydown', this.focusTrapHandler );
	}

	releaseFocus() {
		// Remove focus trap event listener
		if ( this.focusTrapHandler ) {
			this.nav.removeEventListener( 'keydown', this.focusTrapHandler );
			this.focusTrapHandler = null;
		}
	}

	setupSearchIcon() {
		// Focus search input when search icon is clicked
		const searchIcons = this.header.querySelectorAll( '.js-search-submit' );

		searchIcons.forEach( icon => {
			icon.addEventListener( 'click', ( e ) => {

				// Find the corresponding input
				const form = icon.closest( 'form' );
				const input = form ? form.querySelector( '.js-search-input' ) : null;

				if ( !input ) return;

				const value = input.value.trim();

				if ( value === '' ) {
					e.preventDefault();
					input.focus();
				}

			} );
		} );
	}

	setupDropdowns() {
		this.dropdownToggles.forEach( toggle => {
			const parentItem = toggle.closest( '.site-header__item--has-dropdown' );
			const targetId = toggle.getAttribute( 'data-bs-target' );
			const submenu = targetId ? document.querySelector( targetId ) : null;

			if ( !parentItem || !submenu ) return;

			// Mobile: Use Bootstrap Collapse
			// Desktop: Use hover/click with manual class toggle

			// Desktop hover behavior
			parentItem.addEventListener( 'mouseenter', () => {
				if ( window.innerWidth >= 992 ) {
					submenu.classList.add( 'show' );
					toggle.setAttribute( 'aria-expanded', 'true' );
				}
			} );

			parentItem.addEventListener( 'mouseleave', () => {
				if ( window.innerWidth >= 992 ) {
					submenu.classList.remove( 'show' );
					toggle.setAttribute( 'aria-expanded', 'false' );
				}
			} );

			// Click behavior
			toggle.addEventListener( 'click', ( e ) => {
				const isMobile = window.innerWidth < 992;

				if ( !isMobile ) {
					// Desktop: prevent Bootstrap Collapse and use manual toggle
					e.preventDefault();
					e.stopPropagation();

					const isExpanded = toggle.getAttribute( 'aria-expanded' ) === 'true';

					// Close all other dropdowns
					this.dropdownItems.forEach( otherItem => {
						if ( otherItem !== parentItem ) {
							const otherSubmenu = otherItem.querySelector( '.site-header__submenu' );
							const otherToggle = otherItem.querySelector( '.site-header__link--dropdown' );
							if ( otherSubmenu ) otherSubmenu.classList.remove( 'show' );
							if ( otherToggle ) otherToggle.setAttribute( 'aria-expanded', 'false' );
						}
					} );

					// Toggle current dropdown
					submenu.classList.toggle( 'show' );
					toggle.setAttribute( 'aria-expanded', !isExpanded );
				}
				// On mobile, let Bootstrap Collapse handle it (don't preventDefault)
			} );

			// ESC key to close
			toggle.addEventListener( 'keydown', ( e ) => {
				if ( e.key === 'Escape' ) {
					if ( window.innerWidth >= 992 ) {
						// Desktop: manual close
						submenu.classList.remove( 'show' );
						toggle.setAttribute( 'aria-expanded', 'false' );
						toggle.focus();
					} else {
						// Mobile: use Bootstrap Collapse
						const bsCollapse = Collapse.getInstance( submenu );
						if ( bsCollapse && submenu.classList.contains( 'show' ) ) {
							bsCollapse.hide();
						}
					}
				}
			} );
		} );

		// Close dropdowns when clicking outside (desktop only)
		document.addEventListener( 'click', ( e ) => {
			if ( window.innerWidth >= 992 ) {
				const clickedInsideHeader = this.header.contains( e.target );
				if ( !clickedInsideHeader ) {
					this.dropdownItems.forEach( item => {
						const submenu = item.querySelector( '.site-header__submenu' );
						const toggle = item.querySelector( '.site-header__link--dropdown' );
						if ( submenu ) submenu.classList.remove( 'show' );
						if ( toggle ) toggle.setAttribute( 'aria-expanded', 'false' );
					} );
				}
			}
		} );
	}
}

// Initialize when DOM is ready
if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', () => {
		new SiteHeader();
	} );
} else {
	new SiteHeader();
}

export default SiteHeader;
